<template>
    <Spinner v-if="!isFirstTimeAllLoaded" />
    <div
        v-show="isFirstTimeAllLoaded"
        class="betBuilderCombinations"
    >
        <BetBuilderCombinationWorker
            v-for="c in combinations"
            v-bind:key="c.combinationKey"
            v-bind:combination="c"
            v-bind:isEnabled="isEnabledWs"
        />
        <button
            v-if="visibleCombinations.length > 0"
            type="button"
            v-bind:class="['card_header', gaClass]"
            v-on:click="toggleExpand"
        >
            <SboIcon
                name="system:fire"
                class="hotIcon"
                size="16"
            />
            <span class="title">{{ sectionTitle }}</span>
            <SboIcon
                class="arrow"
                v-bind:class="{ _collapsed: !isExpanded }"
                name="system:arrowUp"
                size="16"
            />
        </button>
        <div
            v-show="isExpanded"
            class="betBuilderCombinations-body"
        >
            <Carousel
                v-bind:itemsToShow="1.1"
                v-bind:itemsToScroll="1"
                v-bind:mouseDrag="true"
                v-bind:wrapAround="false"
                name="slide-fade"
                class="betBuilderCombinations-content"
            >
                <Slide
                    v-for="(combination, index) in visibleCombinations"
                    v-bind:key="combination.combinationKey"
                >
                    <slot
                        v-bind:combination="combination"
                        v-bind:order="index + 1"
                    />
                </Slide>
            </Carousel>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed, ref, toRefs } from 'vue';
    import { Carousel, Slide } from 'vue3-carousel';
    import SboIcon from '@/components/common/SboIcon.vue';
    import Spinner from '@/components/common/Spinner.vue';
    import BetBuilderCombinationWorker from '@/components/recommendations/BetBuilderCombinationWorker.vue';
    import { BetBuilderCombinationsContext } from '@/components/recommendations/BetBuilderCombinationsContext';
    import type { IBetBuilderRecommendation } from '@/interface/IBetBuilderRecommendation';
    import { MarketPage } from '@/interface/enum';
    import { useLocalStorage } from '@/libraries/composables/useLocalStorage';
    import { i18n } from '@/libraries/i18n';
    import { useAccountStore } from '@/stores/accountStore';
    import { useRootStore } from '@/stores/rootStore';

    interface Props {
        recommendations: IBetBuilderRecommendation[];
        isLoaded: boolean;
        storageKey?: string;
    }

    const props = defineProps<Props>();

    const { marketPage, isAsi } = storeToRefs(useRootStore());
    const { hashedAccountId } = storeToRefs(useAccountStore());

    const { recommendations } = toRefs(props);
    const {
        combinations,
        visibleCombinations,
        isFirstTimeAllLoaded: _isFirstTimeAllLoaded,
    } = BetBuilderCombinationsContext.provide(recommendations);

    const isFirstTimeAllLoaded = computed(() => {
        if (props.isLoaded && combinations.value.length === 0) {
            return true;
        }
        return _isFirstTimeAllLoaded.value && props.isLoaded;
    });

    const [expandPreferences, setExpandPreferences] = useLocalStorage<Partial<Record<MarketPage, boolean>>>(
        props.storageKey ? `${props.storageKey}:${hashedAccountId.value}` : '',
        {},
    );

    function getBetBuilderExpandPreferenceKey(page: MarketPage): MarketPage {
        switch (page) {
            case MarketPage.EarlyMarket:
            case MarketPage.EarlyMarketPlus1:
            case MarketPage.EarlyMarketPlus2:
            case MarketPage.EarlyMarketPlus3:
            case MarketPage.EarlyMarketPlus4Over:
                return MarketPage.EarlyMarket;
            default:
                return page;
        }
    }

    const localExpanded = ref(true);

    const isExpanded = computed(() => {
        if (props.storageKey) {
            const expandPreferenceKey = getBetBuilderExpandPreferenceKey(marketPage.value);
            return expandPreferences.value[expandPreferenceKey] ?? true;
        }
        return localExpanded.value;
    });

    const isEnabledWs = computed(() => isExpanded.value && isAsi.value);
    const gaClass = computed(() => (isExpanded.value ? 'PreBuilt_Btn_Collapse' : 'PreBuilt_Btn_Expand'));
    const sectionTitle = computed(() => i18n.t('popular_bet_builder_card_title'));

    function toggleExpand() {
        if (props.storageKey) {
            const expandPreferenceKey = getBetBuilderExpandPreferenceKey(marketPage.value);
            setExpandPreferences({
                ...expandPreferences.value,
                [expandPreferenceKey]: !isExpanded.value,
            });
        } else {
            localExpanded.value = !localExpanded.value;
        }
    }
</script>

<style lang="scss" scoped>
@import "vue3-carousel/dist/carousel.css";
@import "@/scss/components/betBuilderCombinations";
</style>
